import React from "react";
import { Card, ProgressBar, Row, Col, Badge } from "react-bootstrap";
import {
  formatUnits,
  formatUnitsExact,
  formatPercentOfLimit,
  getStatusVariant,
  getStatusLabel,
  progressValue,
  USAGE_STATUS,
} from "../../utils/usageUtils";

/**
 * Where the client sits against their tier limit this month, plus where the current
 * run rate would land them by month end.
 */
function TierProgress({ usage }) {
  if (!usage) return null;

  const current = usage.currentMonth;
  const untiered = !usage.hasTier || !(usage.effectiveLimit > 0);

  if (untiered) {
    return (
      <Card className="mb-4 border-secondary">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <div className="fw-semibold">No tier assigned</div>
              <div className="text-muted small">
                Usage is being recorded, but there is no limit to measure it against. Assign a tier
                to track this client against a plan.
              </div>
            </div>
            <div className="text-end">
              <div className="fs-4 fw-semibold">{formatUnits(current?.usageUnits)}</div>
              <div className="text-muted small">units this month</div>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }

  const status = current?.status;
  const projectedStatus = current?.projectedStatus;
  const variant = getStatusVariant(status);

  // Worth calling out explicitly: a client can look fine today and still be on course
  // to breach, which is the whole point of tracking this before pricing changes.
  const projectedBreachAhead =
    projectedStatus === USAGE_STATUS.OVER && status !== USAGE_STATUS.OVER;

  return (
    <Card className={`mb-4 border-${variant}`}>
      <Card.Body>
        <Row className="align-items-center g-3">
          <Col md={6}>
            <div className="d-flex align-items-center gap-2 mb-1">
              <span className="fw-semibold">{usage.tierName}</span>
              <Badge bg={variant}>{getStatusLabel(status)}</Badge>
            </div>
            <div className="text-muted small">
              {formatUnitsExact(current?.usageUnits)} of {formatUnitsExact(usage.effectiveLimit)} units
              {usage.warnPercent ? ` · warns at ${usage.warnPercent}%` : ""}
            </div>
          </Col>

          <Col md={6}>
            <div className="d-flex justify-content-between small mb-1">
              <span>{formatPercentOfLimit(current?.percentOfLimit)} used</span>
              <span className="text-muted">
                {current?.daysElapsed} of {current?.daysInMonth} days
              </span>
            </div>
            <ProgressBar
              now={progressValue(current?.percentOfLimit)}
              variant={variant}
              aria-label="Percent of tier limit used this month"
            />
          </Col>
        </Row>

        <hr className="my-3" />

        <Row className="g-3">
          <Col md={4}>
            <div className="text-muted small">Projected month end</div>
            <div className="fw-semibold">
              {formatUnits(current?.projectedUnits)}{" "}
              <span className={`small text-${getStatusVariant(projectedStatus)}`}>
                ({formatPercentOfLimit(current?.projectedPercentOfLimit)})
              </span>
            </div>
          </Col>
          <Col md={8}>
            {projectedBreachAhead ? (
              <div className="text-danger small">
                On the current run rate this client will pass their limit before the month ends.
              </div>
            ) : (
              <div className="text-muted small">
                Projected from {current?.daysElapsed} day{current?.daysElapsed === 1 ? "" : "s"} of
                a {current?.daysInMonth}-day month.
              </div>
            )}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}

export default TierProgress;
